"use client"
import { useNumberFormatter } from "@opensea/i18n"
import { usePathname } from "@opensea/navigation"
import { classNames, Flex, Pill, tv, type VariantProps } from "@opensea/ui-kit"
import { toLowerCase } from "@opensea/utils"
import { useTranslations } from "next-intl"
import { withSuspense } from "@/components/suspense/WithSuspense"
import { QUERY_PARAM_KEYS } from "@/hooks/query-param/keys"
import { useCategoryQueryParam } from "@/hooks/query-param/use-category-query-param"
import { useClearFilters } from "@/hooks/query-param/use-clear-filters"
import {
  type CollectionOfferStatus,
  useCollectionOfferStatusQueryParam,
} from "@/hooks/query-param/use-collection-offer-status-query-param"
import { useCurrencyCategoryQueryParam } from "@/hooks/query-param/use-currency-category-query-param"
import { useContractStandardQueryParam } from "@/hooks/query-param/use-editable-collections-contract-type-query-param"
import { useEditableCollectionsStatusQueryParam } from "@/hooks/query-param/use-editable-collections-status-query-param"
import { useEditableCollectionsTypeQueryParam } from "@/hooks/query-param/use-editable-collections-type-query-param"
import { useHasMerchandisingQueryParam } from "@/hooks/query-param/use-has-merchandising-query-param"
import { useHasNftQueryParam } from "@/hooks/query-param/use-has-nft-query-param"
import { useIsVerifiedQueryParam } from "@/hooks/query-param/use-is-verified-query-param"
import { useNumericTraitsQueryParam } from "@/hooks/query-param/use-numeric-traits-query-param"
import { useOfferStatusQueryParam } from "@/hooks/query-param/use-offer-status-query-param"
import { useOfferTypeQueryParam } from "@/hooks/query-param/use-offers-type-query-param"
import { useQuantityOwnedQueryParam } from "@/hooks/query-param/use-quantity-owned-query-param"
import { useWatchlistKindQueryParam } from "@/hooks/query-param/use-watchlist-kind-query-param"
import {
  useActivityEnumName,
  useCategoryEnumName,
  useContractStandardEnumName,
  useEditableCollectionsStatusEnumName,
  useEditableCollectionsTypeEnumName,
  useOfferStatusEnumName,
  useOfferTypeEnumName,
} from "@/hooks/use-enum-name"
import { useActivityTypeQueryParam } from "../../hooks/query-param/use-activity-type-query-param"
import { useChainsQueryParam } from "../../hooks/query-param/use-chains-query-param"
import { useCurrencyQueryParam } from "../../hooks/query-param/use-currency-query-param"
import { useMarketQueryParam } from "../../hooks/query-param/use-market-query-param"
import { usePriceQueryParam } from "../../hooks/query-param/use-price-query-param"
import { useRarityQueryParam } from "../../hooks/query-param/use-rarity-query-param"
import { useRelayersQueryParam } from "../../hooks/query-param/use-relayers-query-param"
import { useSearchQueryQueryParam } from "../../hooks/query-param/use-search-query-query-param"
import type { Status } from "../../hooks/query-param/use-status-query-param"
import { useStatusQueryParam } from "../../hooks/query-param/use-status-query-param"
import { useTraitsQueryParam } from "../../hooks/query-param/use-traits-query-param"
import { isTokenStatsPage } from "../stats/utils"
import { ChainSearchPill } from "./ChainSearchPill"
import { CollectionSearchPills } from "./CollectionSearchPills"
import { isFilterApplicable } from "./filter-applicability"
import { useHasSearchFilters, useResetScrollOnSearchPills } from "./hooks"
import { MarketplaceSearchPill } from "./MarketplaceSearchPill"
import { RelayerSearchPill } from "./RelayerSearchPill"

export const searchPillsVariants = tv({
  base: "scrollbar-hidden inline-flex items-center gap-3 overflow-y-scroll *:select-text md:flex-wrap",
  variants: {
    variant: {
      default: "min-h-filter-search-pills",
      "filter-layout-toolbar": "min-h-filter-search-pills",
      "inline-filter-layout-toolbar": "",
    },
  },
})

const STATUS_LABEL: Record<Status, string> = {
  hidden: "Hidden",
  all: "All",
  listed: "Listed",
  ownedByYou: "Owned by you",
  notListed: "Not listed",
}

const COLLECTION_OFFER_STATUS_LABEL: Record<CollectionOfferStatus, string> = {
  yourOffers: "Your offers",
  all: "All offers",
}

type Props = {
  className?: string
  wrap?: "wrap" | "nowrap"
  groupTraits?: boolean
} & Pick<VariantProps<typeof searchPillsVariants>, "variant">

const getRangeLabel = ({
  min,
  max,
  suffix,
  numberFormatter,
}: {
  min?: number
  max?: number
  suffix: string
  numberFormatter: ReturnType<typeof useNumberFormatter>
}) => {
  if (min === undefined && max !== undefined) {
    return `< ${numberFormatter(max, { display: "quantity" })}${suffix ? ` ${suffix}` : ""}`
  }
  if (min !== undefined && max === undefined) {
    return `> ${numberFormatter(min, { display: "quantity" })}${suffix ? ` ${suffix}` : ""}`
  }

  return `${numberFormatter(min, { display: "quantity" })} - ${numberFormatter(max, { display: "quantity" })}${suffix ? ` ${suffix}` : ""}`
}

export const SearchPills = withSuspense(
  ({ variant = "default", className, groupTraits = false }: Props) => {
    const t = useTranslations("SearchPills")
    const numberFormatter = useNumberFormatter()
    const { statuses, onSelectStatus } = useStatusQueryParam()
    const { offerStatuses, onSelectOfferStatus } = useOfferStatusQueryParam()
    const { collectionOfferStatuses, onSelectCollectionOfferStatus } =
      useCollectionOfferStatusQueryParam()
    const { activityTypes, onSelectActivityType } = useActivityTypeQueryParam()

    const { traits, onSelectTrait, onRemoveTraitType } = useTraitsQueryParam()
    const { markets: selectedMarkets, onSelectMarket } = useMarketQueryParam()
    const { currency, onSelectCurrency } = useCurrencyQueryParam()
    const { rarity, setRarity } = useRarityQueryParam()
    const { quantityOwned, setQuantityOwned } = useQuantityOwnedQueryParam()
    const { category, onSelectCategory } = useCategoryQueryParam()
    const {
      category: currencyCategory,
      onSelectCategory: onSelectCurrencyCategory,
    } = useCurrencyCategoryQueryParam()
    const { price, setPrice } = usePriceQueryParam(QUERY_PARAM_KEYS.price)
    const { price: floorPrice, setPrice: setFloorPrice } = usePriceQueryParam(
      QUERY_PARAM_KEYS.floorPrice,
    )
    const { price: topOffer, setPrice: setTopOffer } = usePriceQueryParam(
      QUERY_PARAM_KEYS.topOffer,
    )
    const { price: marketCap, setPrice: setMarketCap } = usePriceQueryParam(
      QUERY_PARAM_KEYS.marketCap,
    )
    const { chains: selectedChains, onSelectChain } = useChainsQueryParam()
    const { relayers, onSelectRelayer } = useRelayersQueryParam()
    const { numericTraits, onRemoveNumericTrait } = useNumericTraitsQueryParam()
    const { offerTypes, onSelectOfferType } = useOfferTypeQueryParam()

    const { searchQuery, setSearchQuery } = useSearchQueryQueryParam()
    const { hasNft, toggleHasNft } = useHasNftQueryParam()
    const { isVerified, toggleIsVerified } = useIsVerifiedQueryParam()
    const { hasMerchandising, toggleHasMerchandising } =
      useHasMerchandisingQueryParam()
    const pathname = usePathname()
    const { kind } = useWatchlistKindQueryParam()
    const isTokenPage = isTokenStatsPage(pathname, kind)
    const isGlobalTokenActivityPage = isFilterApplicable("relayers", pathname)

    const filteredStatuses = statuses.filter(s => s !== "all")
    const filteredCollectionOfferStatuses = collectionOfferStatuses.filter(
      s => s !== "all",
    )
    const activityEnumName = useActivityEnumName()
    const offerStatusEnumName = useOfferStatusEnumName()
    const offerTypeEnumName = useOfferTypeEnumName()

    const {
      types: editableCollectionsTypes,
      setTypes: setEditableCollectionsTypes,
    } = useEditableCollectionsTypeQueryParam()
    const editableCollectionsTypeEnumName = useEditableCollectionsTypeEnumName()
    const {
      statuses: editableCollectionsStatuses,
      setStatuses: setEditableCollectionsStatuses,
    } = useEditableCollectionsStatusQueryParam()
    const editableCollectionsStatusEnumName =
      useEditableCollectionsStatusEnumName()
    const {
      contractStandards: editableCollectionsContractStandards,
      setContractStandards: setEditableCollectionsContractStandards,
    } = useContractStandardQueryParam()
    const editableCollectionsContractStandardEnumName =
      useContractStandardEnumName()

    const hasSearchFilters = useHasSearchFilters()
    const clearAll = useClearFilters()

    useResetScrollOnSearchPills()
    const categoryName = useCategoryEnumName()

    if (!hasSearchFilters) {
      return
    }

    return (
      <Flex
        className={classNames(searchPillsVariants({ variant }), className)}
        key={pathname}
      >
        <CollectionSearchPills />

        {searchQuery ? (
          <Pill onClick={() => setSearchQuery(null)}>
            {t("search", { searchQuery })}
          </Pill>
        ) : null}

        {filteredStatuses.map(status => {
          return (
            <Pill key={status} onClick={() => onSelectStatus(status)}>
              {STATUS_LABEL[status]}
            </Pill>
          )
        })}

        {price ? (
          <Pill onClick={() => setPrice(null)}>
            {t("price", {
              price: getRangeLabel({
                ...price,
                suffix: price.currency,
                numberFormatter,
              }),
            })}
          </Pill>
        ) : null}

        {floorPrice ? (
          <Pill onClick={() => setFloorPrice(null)}>
            {t("floorPrice", {
              floorPrice: getRangeLabel({
                ...floorPrice,
                suffix: floorPrice.currency,
                numberFormatter,
              }),
            })}
          </Pill>
        ) : null}

        {topOffer ? (
          <Pill onClick={() => setTopOffer(null)}>
            {t("topOffer", {
              topOffer: getRangeLabel({
                ...topOffer,
                suffix: topOffer.currency,
                numberFormatter,
              }),
            })}
          </Pill>
        ) : null}

        {marketCap ? (
          <Pill onClick={() => setMarketCap(null)}>
            {t("marketCap", {
              marketCap: getRangeLabel({
                ...marketCap,
                suffix: marketCap.currency,
                numberFormatter,
              }),
            })}
          </Pill>
        ) : null}

        {category ? (
          <Pill onClick={() => onSelectCategory(null)}>
            {categoryName(category)}
          </Pill>
        ) : null}

        {currencyCategory ? (
          <Pill onClick={() => onSelectCurrencyCategory(null)}>
            {currencyCategory}
          </Pill>
        ) : null}

        {numericTraits.map(numericTrait => {
          const label = `${numericTrait.traitType}: ${numericTrait.min.toString()} - ${numericTrait.max.toString()}`

          return (
            <Pill
              key={numericTrait.traitType}
              onClick={() => onRemoveNumericTrait(numericTrait.traitType)}
            >
              {label}
            </Pill>
          )
        })}

        {rarity ? (
          <Pill onClick={() => setRarity(null)}>
            {t("rarity", {
              rarity: getRangeLabel({
                ...rarity,
                suffix: "",
                numberFormatter,
              }),
            })}
          </Pill>
        ) : null}

        {quantityOwned ? (
          <Pill onClick={() => setQuantityOwned(null)}>
            {t("quantityOwned", {
              quantityOwned: getRangeLabel({
                ...quantityOwned,
                suffix: "",
                numberFormatter,
              }),
            })}
          </Pill>
        ) : null}

        {hasNft ? (
          <Pill onClick={() => toggleHasNft()}>{t("hasNft")}</Pill>
        ) : null}

        {isVerified ? (
          <Pill onClick={() => toggleIsVerified()}>{t("isVerified")}</Pill>
        ) : null}

        {hasMerchandising ? (
          <Pill onClick={() => toggleHasMerchandising()}>
            {isTokenPage ? t("hasMerchandising") : t("brandedCollectionPage")}
          </Pill>
        ) : null}

        {currency.map(c => (
          <Pill key={c} onClick={() => onSelectCurrency(c)}>
            {c}
          </Pill>
        ))}

        {selectedMarkets.map(seletedMarket => (
          <MarketplaceSearchPill
            key={seletedMarket}
            market={seletedMarket}
            onClick={() => onSelectMarket(seletedMarket)}
          />
        ))}

        {traits.map(trait => {
          if (groupTraits && traits.every(({ values }) => values.length > 1)) {
            return (
              <Pill
                key={trait.traitType}
                onClick={() => onRemoveTraitType(trait.traitType)}
              >
                {trait.traitType}
              </Pill>
            )
          }

          return trait.values.map(value => {
            const label = `${trait.traitType}: ${value}`

            return (
              <Pill
                key={label}
                onClick={() => onSelectTrait(trait.traitType, value)}
              >
                {label}
              </Pill>
            )
          })
        })}

        {activityTypes.map(type => {
          return (
            <Pill
              key={type}
              onClick={async () => {
                await onSelectActivityType(toLowerCase(type))
              }}
            >
              {activityEnumName(type)}
            </Pill>
          )
        })}

        {offerStatuses.map(status => (
          <Pill
            key={status}
            onClick={async () => {
              await onSelectOfferStatus(toLowerCase(status))
            }}
          >
            {offerStatusEnumName(status)}
          </Pill>
        ))}

        {filteredCollectionOfferStatuses.map(status => (
          <Pill
            key={status}
            onClick={async () => {
              await onSelectCollectionOfferStatus(status)
            }}
          >
            {COLLECTION_OFFER_STATUS_LABEL[status]}
          </Pill>
        ))}

        {offerTypes.map(offerType => (
          <Pill
            key={offerType}
            onClick={async () => {
              await onSelectOfferType(offerType)
            }}
          >
            {offerTypeEnumName(offerType)}
          </Pill>
        ))}

        {selectedChains.map(chain => (
          <ChainSearchPill
            chain={chain}
            key={chain}
            onClick={() => onSelectChain(chain)}
          />
        ))}

        {isGlobalTokenActivityPage
          ? relayers
              .filter(r => r !== "all")
              .map(relayer => (
                <RelayerSearchPill
                  key={relayer}
                  onClick={() => onSelectRelayer(relayer)}
                  relayer={relayer}
                />
              ))
          : null}

        {editableCollectionsTypes.map(type => (
          <Pill key={type} onClick={() => setEditableCollectionsTypes(null)}>
            {editableCollectionsTypeEnumName(type)}
          </Pill>
        ))}

        {editableCollectionsStatuses.map(status => (
          <Pill
            key={status}
            onClick={() => setEditableCollectionsStatuses(null)}
          >
            {editableCollectionsStatusEnumName(status)}
          </Pill>
        ))}

        {editableCollectionsContractStandards.map(standard => (
          <Pill
            key={standard}
            onClick={() => setEditableCollectionsContractStandards(null)}
          >
            {editableCollectionsContractStandardEnumName(standard)}
          </Pill>
        ))}

        <Pill onClick={clearAll} withCloseIcon={false}>
          {t("clear")}
        </Pill>
      </Flex>
    )
  },
)
